import { Vendor } from '../types/vendor'

// Helper function to generate Australian ABN (Australian Business Number)
const generateABN = (index: number): string => {
  const base = 10000000000 + index * 123456
  return `${Math.floor(base / 1000000000)} ${Math.floor(
    (base % 1000000000) / 1000000,
  )
    .toString()
    .padStart(3, '0')} ${Math.floor((base % 1000000) / 1000)
    .toString()
    .padStart(3, '0')} ${(base % 1000).toString().padStart(3, '0')}`
}

// Vendor name templates by category
const vendorCategories = {
  technology: [
    'Tech Solutions',
    'Digital Systems',
    'Cloud Services',
    'Software Corp',
    'IT Solutions',
    'Data Systems',
    'Cyber Security',
    'Network Solutions',
    'Computing Services',
    'Tech Innovations',
  ],
  office: [
    'Office Supplies',
    'Business Essentials',
    'Workplace Solutions',
    'Corporate Supplies',
    'Office Depot',
    'Stationery Co',
    'Business Products',
    'Office Mart',
    'Workspace Solutions',
  ],
  facilities: [
    'Facility Services',
    'Building Maintenance',
    'Property Services',
    'Cleaning Solutions',
    'Maintenance Co',
    'Janitorial Services',
    'Facility Management',
    'Building Care',
  ],
  professional: [
    'Consulting Group',
    'Professional Services',
    'Advisory Partners',
    'Business Consultants',
    'Legal Services',
    'Accounting Firm',
    'Audit Partners',
    'Tax Advisors',
    'HR Solutions',
  ],
  utilities: [
    'Energy Solutions',
    'Utilities Corp',
    'Power Services',
    'Water Services',
    'Telecommunications',
    'Internet Services',
    'Phone Systems',
    'Utility Management',
  ],
  logistics: [
    'Logistics Solutions',
    'Freight Services',
    'Shipping Co',
    'Transport Services',
    'Courier Express',
    'Delivery Solutions',
    'Supply Chain',
    'Distribution Services',
  ],
  catering: [
    'Catering Services',
    'Food Solutions',
    'Corporate Catering',
    'Event Catering',
    'Hospitality Services',
    'Meal Solutions',
    'Café Services',
    'Beverage Co',
  ],
  marketing: [
    'Marketing Agency',
    'Creative Solutions',
    'Digital Marketing',
    'Brand Agency',
    'Advertising Co',
    'Media Services',
    'Design Studio',
    'PR Agency',
  ],
  manufacturing: [
    'Manufacturing Co',
    'Industrial Solutions',
    'Production Services',
    'Factory Supplies',
    'Equipment Manufacturing',
    'Component Suppliers',
    'Industrial Equipment',
  ],
  healthcare: [
    'Medical Supplies',
    'Healthcare Solutions',
    'Pharmaceutical Co',
    'Medical Equipment',
    'Health Services',
    'Clinical Supplies',
    'Wellness Products',
  ],
  construction: [
    'Construction Services',
    'Building Contractors',
    'Engineering Solutions',
    'Project Builders',
    'Construction Materials',
    'Trade Services',
    'Renovation Co',
  ],
  retail: [
    'Retail Solutions',
    'Store Supplies',
    'Merchandise Co',
    'Retail Services',
    'Shop Fittings',
    'Display Solutions',
    'Point of Sale',
  ],
  financial: [
    'Financial Services',
    'Banking Solutions',
    'Investment Group',
    'Finance Corp',
    'Payment Solutions',
    'Treasury Services',
    'Capital Partners',
  ],
  insurance: [
    'Insurance Services',
    'Risk Management',
    'Insurance Brokers',
    'Coverage Solutions',
    'Underwriting Services',
    'Claims Management',
  ],
  training: [
    'Training Solutions',
    'Education Services',
    'Learning Center',
    'Development Programs',
    'Skills Training',
    'Corporate Training',
    'Professional Development',
  ],
}

const prefixes = [
  'Global',
  'Premier',
  'Elite',
  'Advanced',
  'Professional',
  'Quality',
  'Reliable',
  'Trusted',
  'Expert',
  'Superior',
]
const suffixes = [
  'Ltd',
  'Pty Ltd',
  'Inc',
  'Corp',
  'Group',
  'Partners',
  'Associates',
  'International',
  'Australia',
  'Services',
]
const locations = [
  'Sydney',
  'Melbourne',
  'Brisbane',
  'Perth',
  'Adelaide',
  'Canberra',
  'Hobart',
  'Darwin',
  'Gold Coast',
  'Newcastle',
]

// Generate 500 vendors with realistic distribution
const generateVendors = (): Vendor[] => {
  const vendors: Vendor[] = []
  let vendorIndex = 0

  // Helper to create a vendor
  const createVendor = (
    category: string,
    baseName: string,
    scope: 'all' | 'entity' | 'site',
    entityIds?: string[],
    siteIds?: string[],
  ): Vendor => {
    vendorIndex++
    const code = `${category.substring(0, 3).toUpperCase()}${vendorIndex.toString().padStart(4, '0')}`

    return {
      id: `vendor-${vendorIndex}`,
      code,
      name: baseName,
      scope,
      entityIds,
      siteIds,
      isActive: Math.random() > 0.05, // 95% active
      taxId:
        scope !== 'site' || Math.random() > 0.3
          ? generateABN(vendorIndex)
          : undefined,
      paymentTerms: [
        'Net 7',
        'Net 14',
        'Net 15',
        'Net 30',
        'Net 45',
        'Net 60',
        'Due on Receipt',
      ][Math.floor(Math.random() * 7)],
    }
  }

  // Generate global vendors (40% - 200 vendors)
  // These are available to all entities and sites
  Object.entries(vendorCategories).forEach(([category, names]) => {
    const globalCount = Math.floor(names.length * 2) // 2x each name template
    for (let i = 0; i < globalCount && vendorIndex < 200; i++) {
      const nameTemplate = names[i % names.length]
      const prefix =
        i % 3 === 0 ? prefixes[Math.floor(Math.random() * prefixes.length)] : ''
      const suffix = suffixes[Math.floor(Math.random() * suffixes.length)]
      const fullName = `${prefix} ${nameTemplate} ${suffix}`
        .trim()
        .replace(/\s+/g, ' ')

      vendors.push(createVendor(category, fullName, 'all'))
    }
  })

  // Generate entity-specific vendors (35% - 175 vendors)
  // Distributed across 3 entities
  const entityIds = ['entity-1', 'entity-2', 'entity-3']
  for (let i = 0; i < 175; i++) {
    const categoryKeys = Object.keys(vendorCategories)
    const category =
      categoryKeys[Math.floor(Math.random() * categoryKeys.length)]
    const names = vendorCategories[category as keyof typeof vendorCategories]
    const nameTemplate = names[Math.floor(Math.random() * names.length)]

    // Randomly assign to 1 or 2 entities
    const numEntities = Math.random() > 0.7 ? 2 : 1
    const selectedEntities = entityIds
      .sort(() => Math.random() - 0.5)
      .slice(0, numEntities)

    const location = locations[Math.floor(Math.random() * locations.length)]
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)]
    const fullName = `${location} ${nameTemplate} ${suffix}`.trim()

    vendors.push(createVendor(category, fullName, 'entity', selectedEntities))
  }

  // Generate site-specific vendors (25% - 125 vendors)
  // Distributed across 5 sites
  const siteIds = ['site-1', 'site-2', 'site-3', 'site-4', 'site-5']
  const localCategories = ['facilities', 'catering', 'utilities', 'retail']

  for (let i = 0; i < 125; i++) {
    const category =
      localCategories[Math.floor(Math.random() * localCategories.length)]
    const names = vendorCategories[category as keyof typeof vendorCategories]
    const nameTemplate = names[Math.floor(Math.random() * names.length)]

    // Randomly assign to 1-3 sites
    const numSites = Math.random() > 0.8 ? 3 : Math.random() > 0.5 ? 2 : 1
    const selectedSites = siteIds
      .sort(() => Math.random() - 0.5)
      .slice(0, numSites)

    const location = locations[Math.floor(Math.random() * locations.length)]
    const descriptor = [
      'Local',
      'Regional',
      'Community',
      'Neighborhood',
      'District',
    ][Math.floor(Math.random() * 5)]
    const fullName = `${descriptor} ${location} ${nameTemplate}`.trim()

    vendors.push(
      createVendor(category, fullName, 'site', undefined, selectedSites),
    )
  }

  return vendors
}

export const mockVendors: Vendor[] = generateVendors()

// Export summary statistics for debugging
export const vendorStats = {
  total: mockVendors.length,
  byScope: {
    all: mockVendors.filter((v) => v.scope === 'all').length,
    entity: mockVendors.filter((v) => v.scope === 'entity').length,
    site: mockVendors.filter((v) => v.scope === 'site').length,
  },
  active: mockVendors.filter((v) => v.isActive).length,
  inactive: mockVendors.filter((v) => !v.isActive).length,
}
